import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Alert } from 'src/domains/common/components/alert/alert';
import Loading from 'src/components/Loading/Loading';
import { CustomDropdown, FilterOptionType } from 'src/components/RealityCheckForm/CustomDropdown';
import { useAppStateContext } from 'src/AppState/AppState';
import { ComplianceCurrencyType } from 'src/api_openapi/v2/compliance/schemas';
import { GlobalConfigState } from 'src/components/LicenseConfig/GlobalConfig/GlobalConfigState';
import { ComplianceConfigurationLicenseConfigDisplay } from 'src/components/ComplianceConfiguration/LicenseConfig/ComplianceConfigurationLicenseConfigDisplay';
import {
    ComplianceConfigurationLicenseConfigCurrencyField,
    ComplianceConfigurationLicenseConfigCurrencyFieldLabel,
    ComplianceConfigurationLicenseConfigDescription,
    ComplianceConfigurationLicenseConfigSection,
    ComplianceConfigurationLicenseConfigSectionTitle,
    ComplianceConfigurationLicenseConfigWrapper,
} from 'src/components/ComplianceConfiguration/LicenseConfig/ComplianceConfigurationLicenseConfig.style';

const ALL_CURRENCIES: ComplianceCurrencyType[] = ['EUR', 'GBP', 'XTS'];

export const ComplianceConfigurationLicenseConfig = observer((): JSX.Element => {
    const appState = useAppStateContext();
    const [state] = useState(() => new GlobalConfigState(appState));

    useEffect(() => () => state.dispose(), [state]);

    const currencyOptions = useMemo((): Array<FilterOptionType<ComplianceCurrencyType>> => {
        return ALL_CURRENCIES.map((currency) => ({
            value: currency,
            label: currency,
        }));
    }, []);

    if (state.isLoading) {
        return (
            <ComplianceConfigurationLicenseConfigWrapper data-test='compliance-configuration-license-config-loading'>
                <Loading active />
            </ComplianceConfigurationLicenseConfigWrapper>
        );
    }

    return (
        <ComplianceConfigurationLicenseConfigWrapper data-test='compliance-configuration-license-config'>
            <Alert
                icon='info'
                title='Global license configuration'
                variant='default'
            >
                Configure global license thresholds used for compliance checks across the platform.
            </Alert>

            <ComplianceConfigurationLicenseConfigDescription>
                Select a currency and update the trigger values for deposits and withdrawals.
            </ComplianceConfigurationLicenseConfigDescription>

            {state.errorMessage === null || state.errorMessage === '' ? null : (
                <Alert
                    icon='info'
                    title={state.errorMessage}
                    variant='destructive'
                />
            )}

            <ComplianceConfigurationLicenseConfigSection data-test='compliance-configuration-license-config-currency'>
                <ComplianceConfigurationLicenseConfigSectionTitle>
                    Currency
                </ComplianceConfigurationLicenseConfigSectionTitle>
                <ComplianceConfigurationLicenseConfigCurrencyField>
                    <ComplianceConfigurationLicenseConfigCurrencyFieldLabel>
                        Which currency would you like to display the configuration for?
                    </ComplianceConfigurationLicenseConfigCurrencyFieldLabel>
                    <CustomDropdown
                        dataTest='compliance-configuration-license-config-currency'
                        hasInternalLabel={false}
                        onChangeCallback={(value): void => {
                            if (value !== null) {
                                state.selectCurrency(value);
                            }
                        }}
                        optionsList={(): Array<FilterOptionType<ComplianceCurrencyType>> => currencyOptions}
                        placeholder='Select currency'
                        selectedValue={(): ComplianceCurrencyType | null => state.selectedCurrency}
                    />
                </ComplianceConfigurationLicenseConfigCurrencyField>
            </ComplianceConfigurationLicenseConfigSection>

            <ComplianceConfigurationLicenseConfigDisplay state={state} />
        </ComplianceConfigurationLicenseConfigWrapper>
    );
});
